import React, { useEffect, useRef, useState } from 'react'
import { Body } from 'components/Shell/Shell'
import { DumbField } from 'components/Wizard2/Field'
import { TextInput } from 'components/Wizard2/fieldComponents/Form'
import FormCard from 'components/FormCard/FormCard'
import { MI_REQUEST_RECORD_ACCESS, GET_REQUEST_ACCESS_CONFIG } from 'requests'
import { Checkbox } from '@tecuity/granite-ui/Checkbox'
import { DatePicker } from '@tecuity/granite-ui/'
import CalendarIcon from '@tecuity/sos-icons/react/calendar'
import Turnstile from 'components/Turnstile/Turnstile'
import { Spinner } from 'components/Loaders/Loaders'
import { format, isValid } from 'date-fns'
import { useDispatch } from 'react-redux'
import { setSuccessToast } from '../../actions'
import '../Certificates/Certificate.scss'
import '../RequestRecordAccess/RequestRecordAccess.scss'

const RequestAccess = ({ location, history }) => {
  const { sourceId, sourceTypeId, recordNum, category, registrationDate } =
    (location && location.state) || {}

  const properCaseCategory = category ? category.charAt(0).toUpperCase() + category.slice(1) : ''

  const dispatch = useDispatch()

  const [identificationNum, setIdentificationNum] = useState('')
  const [filingDate, setFilingDate] = useState('')
  const [showDatePicker, setShowDatePicker] = useState(false)
  const [errors, setErrors] = useState({})
  const [requestError, setRequestError] = useState(null)
  const [attestations, setAttestations] = useState([])
  const [checkedDeclarations, setCheckedDeclarations] = useState([])
  const [turnstileToken, setTurnstileToken] = useState('')
  const [turnstileSiteKey, setTurnstileSiteKey] = useState('')
  const [turnstileLoadError, setTurnstileLoadError] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const turnstileRef = useRef()

  useEffect(() => {
    GET_REQUEST_ACCESS_CONFIG(
      (config) => {
        if (config && config.Attestations) {
          setAttestations(config.Attestations.slice(0, 2)) // max of 2
        }
        if (config && config.Turnstile) {
          setTurnstileSiteKey(config.Turnstile.SiteKey)
        }
      },
      (err) => {
        console.error(err)
        setAttestations([])
      }
    )
  }, [])

  const canSubmit =
    !!identificationNum &&
    filingDate.length === 10 &&
    checkedDeclarations.length === attestations.length &&
    !!turnstileToken

  const checkEnter = (e) => {
    if (e.charCode === 13) {
      validate()
    }
  }

  const onDateChange = (value) => {
    if (typeof value === 'string') {
      const date = value && isValid(new Date(value)) ? new Date(value) : null
      if (value.length < 10) {
        setFilingDate(value)
      } else {
        setFilingDate(date ? format(date, 'MM/dd/yyyy') : value)
      }
    }
    setShowDatePicker(false)
  }

  const setDateFieldValue = (value) => {
    let v = value.replace(/\D/g, '')
    if (v.length >= 5) {
      v = `${v.slice(0, 2)}/${v.slice(2, 4)}/${v.slice(4)}`
    } else if (v.length >= 3) {
      v = `${v.slice(0, 2)}/${v.slice(2)}`
    }
    v = v.slice(0, 10)
    onDateChange(v)
  }

  const getDateParts = (value) => {
    if (typeof value !== 'string') return null
    const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/)
    if (isoMatch) {
      const [, year, month, day] = isoMatch
      return { year, month, day }
    }
    const slashMatch = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/)
    if (slashMatch) {
      const [, month, day, year] = slashMatch
      return { year, month, day }
    }
    return null
  }

  const datesMatch = (a, b) => {
    const partsA = getDateParts(a)
    const partsB = getDateParts(b)
    if (!partsA || !partsB) return false
    return partsA.year === partsB.year && partsA.month === partsB.month && partsA.day === partsB.day
  }

  const validate = () => {
    const newErrors = {}
    if (!identificationNum) newErrors.identificationNum = 'An identification number is required'
    if (!filingDate) newErrors.filingDate = 'An initial filing date is required'
    if (!newErrors.identificationNum && identificationNum !== recordNum) {
      newErrors.identificationNum = "Identification number doesn't match the selected record"
    }
    if (!newErrors.filingDate && !datesMatch(filingDate, registrationDate)) {
      newErrors.filingDate = "Initial filing date doesn't match the selected record"
    }
    if (!turnstileToken) newErrors.turnstile = 'Please allow the CAPTCHA challenge to complete before submitting.'
    setErrors(newErrors)
    if (Object.keys(newErrors).length === 0) {
      requestAccess()
    }
  }

  const requestAccess = () => {
    setRequestError(null)
    setIsSubmitting(true)
    const [month, day, year] = filingDate.split('/')
    const formattedRegistrationDate = `${year}-${month}-${day}T00:00:00.000`
    MI_REQUEST_RECORD_ACCESS(
      sourceId,
      sourceTypeId,
      identificationNum,
      formattedRegistrationDate,
      turnstileToken,
      () => {
        setIsSubmitting(false)
        dispatch(setSuccessToast(`Added to My Records #${recordNum}`))
        history.goBack()
      },
      (err) => {
        setIsSubmitting(false)
        const errorMessage =
          typeof err === 'string' && err
            ? err
            : (err && err.message) || 'We were unable to verify your access to this record. Please try again.'
        setRequestError(errorMessage)
        setTurnstileToken('')
        if (turnstileRef.current) {
          turnstileRef.current.reset()
        }
      }
    )
  }

  return (
    <Body className="flex-center-page verify-certificate">
      <FormCard
        seal
        title={`File Online - ${properCaseCategory ? `${properCaseCategory} ` : ''}`}
        leftColumn={
          !sourceId ? null : (
            <React.Fragment>
              <DumbField
                label={`${properCaseCategory ? `${properCaseCategory} ` : ''}Identification #`}
                fieldId="identificationNum"
                errorMessage={errors.identificationNum ? [errors.identificationNum] : []}
              >
                <TextInput
                  fieldId="identificationNum"
                  value={identificationNum}
                  onChange={setIdentificationNum}
                  onKeyPress={checkEnter}
                />
              </DumbField>
              <DumbField
                label="Initial Filing Date"
                fieldId="filingDate"
                errorMessage={errors.filingDate ? [errors.filingDate] : []}
              >
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <TextInput
                    fieldId="filingDate"
                    placeholder="MM/DD/YYYY"
                    value={filingDate}
                    onChange={setDateFieldValue}
                    onKeyPress={checkEnter}
                    disableSelectionRestoration
                  />
                  <button
                    type="button"
                    className="btn btn-primary btn-raised"
                    style={{ marginLeft: '8px' }}
                    aria-label="Open calendar widget for Initial Filing Date"
                    onClick={() => setShowDatePicker(true)}
                  >
                    <CalendarIcon size="small" />
                  </button>
                </div>
                <DatePicker
                  onChange={onDateChange}
                  isOpen={showDatePicker}
                  onCalendarClose={() => setShowDatePicker(false)}
                  selectedDate={filingDate && isValid(new Date(filingDate)) ? new Date(filingDate) : null}
                />
              </DumbField>
              {attestations.length > 0 && (
                <div className="attestation-checkbox-wrapper" style={{ marginBottom: '20px' }}>
                  {attestations.map((attestation, i) => (
                    <div key={i}>
                      <Checkbox
                        label={attestation}
                        onClick={(e) => {
                          setCheckedDeclarations((cd) =>
                            e.target.checked ? [...cd, i] : cd.filter((d) => d !== i)
                          )
                        }}
                        checked={checkedDeclarations.includes(i)}
                      />
                    </div>
                  ))}
                </div>
              )}
              {turnstileSiteKey && (
                <div style={{ margin: '20px 0' }}>
                  <Turnstile
                    ref={turnstileRef}
                    siteKey={turnstileSiteKey}
                    onVerify={setTurnstileToken}
                    onExpire={() => setTurnstileToken('')}
                    onError={() => setTurnstileLoadError(true)}
                  />
                  {turnstileLoadError ? (
                    <p className="description danger-text">
                      Unable to load the security check. Please refresh the page and try again.
                    </p>
                  ) : (
                    errors.turnstile && <p className="description danger-text">{errors.turnstile}</p>
                  )}
                </div>
              )}
              <button
                className="btn btn-primary btn-raised"
                onClick={validate}
                disabled={!canSubmit || isSubmitting}
                type="submit"
              >
                {isSubmitting ? <Spinner className="button-loader" size="small" /> : 'File Online'}
              </button>
            </React.Fragment>
          )
        }
        rightColumn={
          <React.Fragment>
            {!sourceId ? (
              <p className="description danger-text">
                No record was selected. Please return to the search and try again.
              </p>
            ) : (
              <p className="description">
                Enter the identification number and initial filing date for the record you selected from the search
                to receive access to that record so you can file documents online.
              </p>
            )}
            {requestError && <p className="description danger-text">{requestError}</p>}
          </React.Fragment>
        }
      />
    </Body>
  )
}

export default RequestAccess
